/**
 * Catalogue of Journey question types.
 * Mirrors com.advancedcrm.model.enums.Journey.JourneyQuestionType — keep in sync.
 *
 * group    : how the type picker is grouped in the builder
 * supports : which editor sections apply to the type
 */
export const QUESTION_GROUPS = ['text', 'choice', 'datetime', 'special', 'upsell', 'tasks'];

export const QUESTION_TYPES = [
    // --- text ---
    {
        value: 'SHORT_TEXT', label: 'questiontype_shorttext', icon: 'TypeIcon', group: 'text',
        supports: {placeholder: true},
    },
    {
        value: 'LONG_TEXT', label: 'questiontype_longtext', icon: 'AlignLeftIcon', group: 'text',
        supports: {placeholder: true},
    },
    {
        value: 'NUMBER', label: 'questiontype_number', icon: 'HashIcon', group: 'text',
        supports: {placeholder: true, minMax: true},
    },
    {
        value: 'EMAIL', label: 'questiontype_email', icon: 'MailIcon', group: 'text',
        supports: {placeholder: true, prefill: true},
    },
    {
        value: 'PHONE', label: 'questiontype_phone', icon: 'PhoneIcon', group: 'text',
        supports: {placeholder: true, prefill: true},
    },
    // --- choice ---
    {
        value: 'SELECT', label: 'questiontype_select', icon: 'ChevronDownIcon', group: 'choice',
        supports: {options: true, other: true, products: true},
    },
    {
        value: 'RADIO_CARDS', label: 'questiontype_radiocards', icon: 'GridIcon', group: 'choice',
        supports: {options: true, other: true, products: true},
    },
    {
        value: 'CHECKBOX_MULTI', label: 'questiontype_checkboxmulti', icon: 'CheckSquareIcon', group: 'choice',
        supports: {options: true, other: true, products: true, multiple: true},
    },
    {
        value: 'YES_NO', label: 'questiontype_yesno', icon: 'ToggleLeftIcon', group: 'choice',
        supports: {},
    },
    // --- date & time ---
    {
        value: 'DATE', label: 'questiontype_date', icon: 'CalendarIcon', group: 'datetime',
        supports: {dateRange: true, prefill: true},
    },
    {
        value: 'TIME', label: 'questiontype_time', icon: 'ClockIcon', group: 'datetime',
        supports: {},
    },
    {
        value: 'DATETIME', label: 'questiontype_datetime', icon: 'CalendarIcon', group: 'datetime',
        supports: {dateRange: true},
    },
    // --- special ---
    {
        value: 'RATING', label: 'questiontype_rating', icon: 'StarIcon', group: 'special',
        supports: {rating: true},
    },
    {
        value: 'CONSENT', label: 'questiontype_consent', icon: 'ShieldIcon', group: 'special',
        supports: {},
    },
    {
        value: 'INFO', label: 'questiontype_info', icon: 'InfoIcon', group: 'special',
        supports: {content: true, noAnswer: true},
    },
    {
        value: 'FILE_UPLOAD', label: 'questiontype_fileupload', icon: 'UploadCloudIcon', group: 'special',
        supports: {file: true},
    },
    // --- upsell ---
    {
        value: 'PRODUCT_PICKER', label: 'questiontype_productpicker', icon: 'ShoppingBagIcon', group: 'upsell',
        supports: {productList: true, multiple: true},
    },
    // --- booking tasks: replace the old post-booking alert modal ---
    {
        value: 'GUEST_DETAILS', label: 'questiontype_guestdetails', icon: 'UserCheckIcon', group: 'tasks',
        supports: {action: true},
    },
    {
        value: 'IDENTITY_VERIFICATION', label: 'questiontype_identity', icon: 'CreditCardIcon', group: 'tasks',
        supports: {action: true},
    },
    {
        value: 'PAYMENT', label: 'questiontype_payment', icon: 'DollarSignIcon', group: 'tasks',
        supports: {action: true, payment: true},
    },
    {
        value: 'SIGNATURE', label: 'questiontype_signature', icon: 'EditIcon', group: 'tasks',
        supports: {action: true},
    },
];

/** Which contract payment a PAYMENT step asks for — mirrors JourneyPaymentType. */
export const PAYMENT_TYPES = [
    {value: 'AUTHORIZATION_FEE', label: 'paymenttype_authorization'},
    {value: 'RENT', label: 'paymenttype_rent'},
    {value: 'DEPOSIT_FEE', label: 'paymenttype_deposit'},
    {value: 'SERVICE_FEE', label: 'paymenttype_service'},
    {value: 'HANDLING_FEE', label: 'paymenttype_handling'},
    {value: 'COMMISSION_FEE', label: 'paymenttype_commission'},
];

export function isActionType(value) {
    return supports(value, 'action');
}

const TYPE_MAP = QUESTION_TYPES.reduce((map, type) => {
    map[type.value] = type;
    return map;
}, {});

export function questionType(value) {
    return TYPE_MAP[value] || null;
}

export function supports(value, feature) {
    const type = TYPE_MAP[value];
    return !!(type && type.supports && type.supports[feature]);
}

export function typesByGroup(group) {
    return QUESTION_TYPES.filter(type => type.group === group);
}

/** Operators for conditional logic — mirrors JourneyConditionOperator. */
export const CONDITION_OPERATORS = [
    {value: 'EQUALS', label: 'equals'},
    {value: 'NOT_EQUALS', label: 'notequals'},
    {value: 'CONTAINS', label: 'contains'},
    {value: 'ANSWERED', label: 'answered', presence: true},
    {value: 'NOT_ANSWERED', label: 'notanswered', presence: true},
    // ordering (times, dates, numbers); value side supports {checkInTime}/{checkOutTime}
    {value: 'BEFORE', label: 'isbefore', ordering: true},
    {value: 'AFTER', label: 'isafter', ordering: true},
];

/** Targets a journey can aim at — mirrors JourneyScope (specificity ascending). */
export const JOURNEY_SCOPES = [
    {value: 'ORGANIZATION', label: 'scopeorganization', icon: 'GlobeIcon', variant: 'secondary'},
    {value: 'BRANCH', label: 'scopebranch', icon: 'GitBranchIcon', variant: 'info'},
    {value: 'BUILDING', label: 'scopebuilding', icon: 'LayersIcon', variant: 'primary'},
    {value: 'ROOM_CATEGORY', label: 'scoperoomcategory', icon: 'TagIcon', variant: 'warning'},
    {value: 'ROOM', label: 'scoperoom', icon: 'HomeIcon', variant: 'success'},
];

export const PREFILL_OPTIONS = [
    {value: 'NONE', label: 'none'},
    {value: 'GUEST_FIRSTNAME', label: 'firstname'},
    {value: 'GUEST_LASTNAME', label: 'lastname'},
    {value: 'GUEST_EMAIL', label: 'email'},
    {value: 'GUEST_PHONE', label: 'phone'},
    {value: 'CHECKIN_DATE', label: 'checkin'},
    {value: 'CHECKOUT_DATE', label: 'checkout'},
    {value: 'NUMBER_OF_GUESTS', label: 'guests'},
    {value: 'ROOM_NAME', label: 'hm_room'},
];
